import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, Search, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchMusicCatalog,
  fetchPhotos,
  flattenMusicCatalog,
  getCachedMusicReleases,
  getCachedPhotos,
  preloadPhotoCover,
  preloadReleaseArtwork,
  type SlimRelease,
} from '@/lib/api';
import { CatalogFormatLabel } from '@/components/list/CatalogFormatLabel';
import { paths } from '@/lib/routes';
import { runViewTransition } from '@/lib/modeNav';
import { photoMatchesQuery } from '@/components/list/CatalogLists';
import type { PhotoPost } from '@/types';
import { useSiteSearch } from '@/hooks/useSiteSearch';
import { useCatalogRowFit } from '@/hooks/useCatalogRowFit';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function SearchSheet({ open, onClose }: Props) {
  const { query, setQuery, clearQuery } = useSiteSearch();
  const [music, setMusic] = useState<SlimRelease[]>(
    () => getCachedMusicReleases() ?? [],
  );
  const [photos, setPhotos] = useState<PhotoPost[]>(
    () => getCachedPhotos() ?? [],
  );
  const [loading, setLoading] = useState(
    () => getCachedMusicReleases() == null || getCachedPhotos() == null,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 80);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const cachedMusic = getCachedMusicReleases();
    const cachedPhotos = getCachedPhotos();
    if (cachedMusic && cachedPhotos) {
      setMusic(cachedMusic);
      setPhotos(cachedPhotos);
      setLoading(false);
      return;
    }
    setLoading(true);
    void (async () => {
      try {
        const [photoList, catalog] = await Promise.all([
          fetchPhotos(),
          fetchMusicCatalog(),
        ]);
        if (cancelled) return;
        setPhotos(photoList);
        setMusic(flattenMusicCatalog(catalog));
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearQuery();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, clearQuery]);

  const q = query.trim().toLowerCase();
  const musicHits = useMemo(() => {
    if (!q) return [];
    return music.filter((r) => r.name.toLowerCase().includes(q));
  }, [music, q]);
  const photoHits = useMemo(() => {
    if (!q) return [];
    return photos.filter((p) => photoMatchesQuery(p, q));
  }, [photos, q]);
  const hitCount = musicHits.length + photoHits.length;
  const hitsReady = Boolean(q) && !loading;

  useCatalogRowFit(scrollRef, hitCount, open && hitsReady && hitCount > 0);

  const closeAndClear = () => {
    clearQuery();
    onClose();
  };

  const pickRelease = (item: SlimRelease) => {
    void (async () => {
      await preloadReleaseArtwork(item);
      closeAndClear();
      runViewTransition(() => {
        navigate(paths.musicRelease(item.id));
      }, 'forward');
    })();
  };

  const pickPhoto = (item: PhotoPost) => {
    void (async () => {
      await preloadPhotoCover(item);
      closeAndClear();
      runViewTransition(() => {
        navigate(paths.photoPost(item.id));
      }, 'forward');
    })();
  };

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.button
            key="search-backdrop"
            type="button"
            className="search-sheet-backdrop"
            aria-label="Dismiss search"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
            onClick={closeAndClear}
          />
          <motion.div
            key="search-sheet"
            id="mobile-search-sheet"
            className="search-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Search"
            initial={{ y: '-100%' }}
            animate={{ y: 0 }}
            exit={{ y: '-100%' }}
            transition={{ duration: 0.42, ease: [0.32, 0.72, 0, 1] }}
          >
            <div className="search-sheet-chrome">
              <label className="search-sheet-field" htmlFor="mobile-site-search">
                <Search size={16} aria-hidden />
                <input
                  ref={inputRef}
                  id="mobile-site-search"
                  type="search"
                  enterKeyHint="search"
                  placeholder="Search music & photos"
                  aria-label="Search site"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query ? (
                  <button
                    type="button"
                    className="search-sheet-clear"
                    aria-label="Clear search"
                    onClick={() => setQuery('')}
                  >
                    <X size={14} />
                  </button>
                ) : null}
              </label>
              <button
                type="button"
                className="search-sheet-cancel"
                onClick={closeAndClear}
              >
                Cancel
              </button>
            </div>

            <div
              ref={scrollRef}
              className={`search-sheet-results${hitsReady ? ' has-query' : ''}`}
            >
              {!q ? (
                <p className="search-sheet-hint">Search releases and photo sets.</p>
              ) : null}
              {hitsReady && hitCount === 0 ? (
                <p className="menu-search-empty">No results.</p>
              ) : null}
              {hitsReady && musicHits.length ? (
                <>
                  <p className="menu-search-section">Music</p>
                  {musicHits.map((item, i) => {
                    const year = new Date(item.releaseDate).getFullYear();
                    return (
                      <Link
                        key={`m-${item.id}`}
                        to={paths.musicRelease(item.id)}
                        className="catalog-row menu-search-row"
                        style={{ '--result-i': i } as CSSProperties}
                        onClick={(e) => {
                          e.preventDefault();
                          pickRelease(item);
                        }}
                      >
                        <span className="row-year">{year}</span>
                        <span className="row-title">{item.name}</span>
                        <CatalogFormatLabel label={item.type} />
                        <ArrowUpRight size={16} />
                      </Link>
                    );
                  })}
                </>
              ) : null}
              {hitsReady && photoHits.length ? (
                <>
                  <p className="menu-search-section">Photo</p>
                  {photoHits.map((item, i) => {
                    const year = new Date(item.date).getFullYear();
                    return (
                      <Link
                        key={`p-${item.id}`}
                        to={paths.photoPost(item.id)}
                        className="catalog-row menu-search-row"
                        style={
                          {
                            '--result-i': musicHits.length + i,
                          } as CSSProperties
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          pickPhoto(item);
                        }}
                      >
                        <span className="row-year">{year}</span>
                        <span className="row-title">{item.title}</span>
                        <CatalogFormatLabel label={item.format} />
                        <ArrowUpRight size={16} />
                      </Link>
                    );
                  })}
                </>
              ) : null}
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}
